import { useRecordContext } from "react-admin";
import { featureFlags } from "@/utils/featureFlags";
import type { Claimant, ClaimsData, ClaimsValues, EffectiveDeductibleSource, Policy, PolicyData } from "@/types";
import { useSelectedPolicyId } from "../components/PolicyContext";

interface CurrentPolicyData {
  claimant: Claimant | undefined;
  policy: Policy | undefined;
  policyId: string | undefined;
  policyData: PolicyData | undefined;
}

interface SelectedPolicyData {
  policy: Policy | undefined;
  policyId: string | undefined;
  policyData: PolicyData | undefined;
}

/**
 * Hook to get the first/most recent policy and its data from a claimant record.
 * Used in list views where there's no PolicyContext.
 */
export const useCurrentPolicyData = (): CurrentPolicyData => {
  const claimant = useRecordContext<Claimant>();
  const policy = claimant?.policies?.[0] as Policy | undefined;
  const policyId = policy?.id;
  const policyData = policyId ? claimant?.policy_data?.[policyId] : undefined;

  return { claimant, policy, policyId, policyData };
};

/**
 * Hook to get the selected policy and its data from PolicyContext.
 * Used in show/detail views where user can switch between policies.
 */
export const useSelectedPolicyData = (): SelectedPolicyData => {
  const claimant = useRecordContext<Claimant>();
  const selectedPolicyId = useSelectedPolicyId();

  const policy = claimant?.policies?.find((p) => p.id === selectedPolicyId);
  const policyData = selectedPolicyId ? claimant?.policy_data?.[selectedPolicyId] : undefined;

  return { policy, policyId: selectedPolicyId, policyData };
};

/**
 * Returns the authoritative claims values based on the authority field.
 * - If authority is "reported", use reported values
 * - Otherwise, use calculated values with fallback to reported
 */
export const getAuthoritativeClaimsValues = (claims?: ClaimsData): ClaimsValues | undefined => {
  if (!claims) return undefined;

  if (claims.authority === "reported" && claims.reported) {
    return claims.reported;
  }
  return claims.calculated ?? claims.reported;
};

/**
 * Returns the earlier of two optional ISO-8601 dates (ignoring nulls). ISO-8601
 * strings sort lexicographically in chronological order, so a string compare is
 * correct. Used for a carve-out claimant's blended as-of = min(medical, rx) so
 * the total never reads fresher than its stalest component (ADR-077 §5).
 */
const earlierIsoDate = (a?: string, b?: string): string | undefined => {
  if (!a) return b;
  if (!b) return a;
  return a < b ? a : b;
};

/**
 * Resolves a claimant's Aggregate Specific Deductible allocation (#3302, #6829).
 *
 * Reads `claims.calculated` DIRECTLY, and that is deliberate on two counts.
 *
 * It does not go through {@link getResolvedClaimsValues}: that function's
 * carve-out branch returns the server-blended per-type totals, and the blend
 * covers only the `total_*` money fields, so routing ASD through it would drop
 * them to `undefined` for every carve-out claimant.
 *
 * It also does not go through {@link getAuthoritativeClaimsValues}, which is the
 * bug this replaces. That returns `claims.reported` under reported authority,
 * and the engine writes the allocation **only** to `claims.calculated` — so an
 * authority-resolved read returned nothing for every reported-authority
 * claimant on an ASD policy, a live cohort (#6236/#6237/#6238). Reported
 * authority does not suppress an allocation: the engine derives the excess from
 * the reported spend when that is authoritative, then records it on the
 * calculated bundle.
 *
 * SCOPE LIMIT, corrected after review. That fix covers a reported-authority
 * claimant who ALSO has TPA line items. It does NOT cover a carrier-YTD-only
 * claimant, who is never allocated in the first place:
 * `groupStoplossClaimsWithASD` iterates `GetDistinctClaimantsByPolicy`, which is
 * `SELECT DISTINCT claimant_id_ref FROM claim_items` — no items means never
 * iterated, means no allocation, means all three fields still dash after this
 * change. ADR-051's own Context defines the archetypal reported source as "a
 * claimant-level aggregate with NO line items", so that cohort is
 * architecturally expected on exactly the book these tickets came from.
 *
 * This is a backend scoping question, not something this function can fix, and
 * it is tracked on #6829 rather than silently implied to be handled here.
 *
 * Reading `calculated` unconditionally is correct rather than a compromise: an
 * ASD allocation is policy-level, not per-claim-type, so there is exactly one
 * set of amounts per claimant per policy regardless of which ledger is
 * authoritative for their spend.
 *
 * FRESHNESS. These three columns are excluded by design from the shared
 * financial upsert (`queries/claimant_policy_snapshots.sql`: "must NOT be added
 * to the column list") and are written only by the ASD run, whose per-claimant
 * steps are best-effort — a `persistASDAllocation` error is logged and the loop
 * continues. So the financials on a row can be current while its allocation is a
 * run behind, and `reconcileDerivedASDSplit`, the only automated cross-check,
 * was removed by this PR because its premise was the derivation.
 *
 * #6829's review pass closed the worst case: a claimant whose excess drops to
 * <= 0 is now actively NULLed by a clearing pass rather than keeping a stale
 * figure forever, so the "Total YTD 260,000 / Deductible 260,000 / Specific
 * Excess 60,000" contradiction no longer latches. What remains is a one-run lag,
 * not an indefinite one. Note the per-row engine invariant
 * (`excess == consumed + payable`) would NOT detect even that, because a stale
 * allocation is internally consistent; only comparing against the claimant's
 * current `spend_over_deductible` would, which needs care for carve-out
 * claimants where `getResolvedClaimsValues` returns only blended `total_*`.
 *
 * Returns undefined when the claimant has no allocation — a non-ASD policy, or
 * an ASD policy the engine has not run for since the columns landed — so
 * callers render a dash rather than a misleading $0.00. The backend makes that
 * distinguishable: the three fields are nullable columns serialized through
 * `*decimal.Decimal`, so "no allocation" arrives as an **absent key** rather
 * than as 0 (#6829). A real zero allocation arrives as 0 and is meaningful.
 */
export const getResolvedASDValues = (
  policyData?: PolicyData
): Pick<ClaimsValues, "amt_specific_excess" | "amt_asd_consumed" | "amt_carrier_payable"> | undefined => {
  const calculated = policyData?.claims?.calculated;
  if (!calculated) return undefined;

  const { amt_specific_excess, amt_asd_consumed, amt_carrier_payable } = calculated;

  // All three absent means no allocation. They are written as a set by a single
  // engine statement, so a partial set is not a state the backend produces —
  // checking all three keeps this correct if that ever changes rather than
  // reading a half-allocation as a whole one.
  if (amt_specific_excess === undefined && amt_asd_consumed === undefined && amt_carrier_payable === undefined) {
    return undefined;
  }

  // LEGACY-PAYLOAD GUARD, and the reason this PR is not merge-safe on its own
  // without it (Quinton, !4684 review).
  //
  // The absence check above is VALUE-BLIND against the pre-#6829 API, which
  // declares these as value-type `decimal.Decimal`. `omitempty` is a no-op on a
  // struct type and `common/types.go` sets MarshalJSONWithoutQuotes, so all three
  // ALWAYS serialize — as bare `0`. The guard therefore never fires, and every
  // claimant on every ASD policy renders $0.00 for Specific Excess, Applied and
  // Carrier Payable, in the table and in the currency-styled XLSX, with a $0.00
  // SUM subtotal. AggSpecCorridorBanner still reads "Breached" from asd_tracking,
  // so there is no visual tell. That is worse than the dash it replaces.
  //
  // This matters because completing this PR AUTO-DEPLOYS the SPA to sandbox:
  // .pipeline/sandbox-coornis-ui.yml triggers on `main` with no path filter.
  //
  // All three exactly 0 alongside a positive spend_over_deductible is
  // UNREACHABLE on the post-#6829 backend and UNIVERSAL on the old one:
  //
  //   - an allocated claimant always has amt_specific_excess > 0, because the
  //     engine skips the write for a non-positive excess and AllocateASD
  //     guarantees consumed + payable == excess;
  //   - an unallocated claimant is now NULLed rather than zeroed, so their three
  //     fields arrive ABSENT and are caught by the check above.
  //
  // So this is a payload-version discriminator, not a heuristic. Preferred over a
  // build-time feature flag because it also survives a !4683 ROLLBACK, where a
  // flag would still be on. The `> 0` qualifier keeps it narrow: a claimant
  // genuinely below their deductible on the old API has all three 0 AND
  // spend_over_deductible 0, and `undefined` is the right answer for them anyway.
  const legacyAllZero =
    amt_specific_excess === 0 &&
    amt_asd_consumed === 0 &&
    amt_carrier_payable === 0 &&
    (calculated.spend_over_deductible ?? 0) > 0;
  if (legacyAllZero) {
    return undefined;
  }

  return { amt_specific_excess, amt_asd_consumed, amt_carrier_payable };
};

/**
 * Resolves the authoritative claims money values for a policy-data block.
 *
 * - **Bundled** policies delegate to {@link getAuthoritativeClaimsValues} over
 *   the claims subtree — unchanged, so non-carve-out display is byte-for-byte.
 * - **Carve-out** policies (per-type authority resolved, `authority_by_type`
 *   present) read the pre-resolved, server-blended top-level totals
 *   (ADR-077 §5 / story 3799) — the FE never re-derives the blend — and set the
 *   as-of to the earlier of the two per-type paid-through dates.
 *
 * Prefer this over `getAuthoritativeClaimsValues(claims)` at call sites that may
 * render a carve-out claimant.
 */
export const getResolvedClaimsValues = (policyData?: PolicyData): ClaimsValues | undefined => {
  if (!policyData) return undefined;

  const perType = policyData.authority_by_type;
  if (!perType) {
    return getAuthoritativeClaimsValues(policyData.claims);
  }

  return {
    total_paid: policyData.total_paid,
    total_medical: policyData.total_medical,
    total_rx: policyData.total_rx,
    total_denied: policyData.total_denied,
    total_adjustments: policyData.total_adjustments,
    spend_over_deductible: policyData.spend_over_deductible,
    paid_through_date: earlierIsoDate(perType.medical.as_of, perType.rx.as_of),
  };
};

/**
 * What the deductible basis dropped from a claimant's stamped lines on this term
 * (ADR-081 D5.5, #7338), resolved for display on the claimant Total Paid card. Two
 * figures:
 *
 * - `window`: dollars the contract window dropped (paid outside the paid window,
 *   incurred outside the incurred window, or missing a paid or service date).
 * - `notPaid`: dollars on lines whose claim status is not paid — submitted,
 *   processing, approved AND denied (the view's `status_not_paid`).
 *
 * Total Paid + window + notPaid = the sum of the stamped line list, so the tile
 * reconciles with the lines beneath it — when no stamped line is owned by a
 * sibling term and no sibling-stamped line is owned here (Year-1-first, #7574),
 * and Total Paid is the calculated figure. Rules, in order:
 *
 * - Off when the deductible-basis flag is off: there is no window to exclude by.
 * - Only ever read off `claims.calculated`; the reported bundle has no line list.
 * - Bundled policy on reported authority: nothing. The tile shows the carrier's
 *   figure, which the line list never reconciled to.
 * - Carve-out (per-type authority): each figure is summed over the types that
 *   resolve calculated; a reported type contributes nothing, and with both types
 *   reported nothing shows. Never keyed off the raw `claims.authority` scalar,
 *   which stays 'reported' on a carve-out by design.
 * - A missing field is "not computed" (no basis recompute since the columns
 *   shipped) and stays undefined so the caller shows no line; 0 is "computed,
 *   nothing dropped" and renders $0.00.
 */
export interface ExcludedByBasis {
  window?: number;
  notPaid?: number;
}

const sumDefined = (...values: (number | undefined)[]): number | undefined => {
  const present = values.filter((v): v is number => v !== undefined);
  return present.length === 0 ? undefined : present.reduce((a, b) => a + b, 0);
};

export const getExcludedByBasis = (policyData?: PolicyData): ExcludedByBasis | undefined => {
  if (!featureFlags.deductibleBasisViewEnabled || !policyData) return undefined;
  const calculated = policyData.claims?.calculated;
  if (!calculated) return undefined;

  const perType = policyData.authority_by_type;
  let medical = true;
  let rx = true;
  if (perType) {
    medical = perType.medical.authority === "calculated";
    rx = perType.rx.authority === "calculated";
  } else if (policyData.claims?.authority === "reported" && policyData.claims.reported) {
    return undefined;
  }

  const window = sumDefined(
    medical ? calculated.excluded_window_medical : undefined,
    rx ? calculated.excluded_window_rx : undefined
  );
  const notPaid = sumDefined(
    medical ? calculated.excluded_not_paid_medical : undefined,
    rx ? calculated.excluded_not_paid_rx : undefined
  );

  if (window === undefined && notPaid === undefined) return undefined;
  return { window, notPaid };
};

/**
 * The single seam for resolving the specific deductible actually applied to a
 * claimant on a policy — the denominator for "has met deductible" gating and for
 * deductible amount/percentage display. Returns the `amount` plus a human `label`
 * naming the precedence branch that applied.
 *
 * Amount precedence (Feature #5287, resolved in the BE's queries/policy_data.sql):
 * full exclusion (0) > contract laser > straight laser > base specific deductible.
 * The ADR-077 §6/§7 per-type blended-overage recompute consumes this value.
 *
 * - Prefers the BE-resolved `effective_specific_deductible`, which encodes that
 *   precedence server-side. `0` is a valid result (full exclusion), so only
 *   `null`/`undefined` falls through.
 * - Fallback (older/stale snapshots, #5196) recomputes the same precedence from
 *   the policy-data flags — including contract laser / exclusion — so it degrades
 *   to a correct value rather than the pre-#5879 base-only gate.
 *
 * This is the one place the FE resolves an effective deductible; consumers must
 * route through it rather than re-deriving from laser attributes (ADR-077 warns
 * that duplicated resolvers silently diverge).
 */
/**
 * Source of a claimant's effective specific deductible. BASE/LASER/CONTRACT/
 * EXCLUDED are the precedence branches the FE fallback can derive; CONDITIONAL,
 * MODIFICATION and REVIEW_DECISION come only from the server
 * (`effective_deductible_source`, Feature #3502), because recency between a
 * conditional trigger, a modification and a straight laser is resolved in SQL.
 */
export type DeductibleSource =
  | "BASE"
  | "LASER"
  | "CONTRACT"
  | "EXCLUDED"
  | "CONDITIONAL"
  | "MODIFICATION"
  | "REVIEW_DECISION";

/** Server `effective_deductible_source` → FE source. */
const SERVER_DEDUCTIBLE_SOURCE: Record<EffectiveDeductibleSource, DeductibleSource> = {
  specific: "BASE",
  lasered: "LASER",
  conditional_laser: "CONDITIONAL",
  modification: "MODIFICATION",
  contract_laser: "CONTRACT",
  contract_excluded: "EXCLUDED",
  review_decision: "REVIEW_DECISION",
};

/** FE source → server key, for the shared source chip (the inverse of the map above). */
export const toServerDeductibleSource = (source: DeductibleSource): EffectiveDeductibleSource =>
  (Object.keys(SERVER_DEDUCTIBLE_SOURCE) as EffectiveDeductibleSource[]).find(
    (k) => SERVER_DEDUCTIBLE_SOURCE[k] === source
  ) ?? "specific";

const SOURCE_LABEL: Record<DeductibleSource, string> = {
  BASE: "Deductible",
  LASER: "Deductible (Lasered)",
  CONTRACT: "Deductible (Contract)",
  EXCLUDED: "Excluded from Contract",
  CONDITIONAL: "Deductible (Conditional Laser)",
  MODIFICATION: "Deductible (Modified)",
  REVIEW_DECISION: "Deductible (Review Decision)",
};

/**
 * True when the deductible is claimant-specific (any laser kind, a conditional
 * trigger, a modification or a review decision): the cases that show a laser
 * chip. BASE and EXCLUDED do not.
 */
export const isLaserSource = (source: DeductibleSource): boolean => source !== "BASE" && source !== "EXCLUDED";

/**
 * Whether a resolved deductible reads as lasered (chips, the exhibit's lasered
 * flag): {@link isLaserSource}, except a review decision that chose the policy
 * base amount, which is the base deductible by another route.
 */
export const isLaseredDeductible = (
  effective: { source: DeductibleSource; amount: number },
  policy?: DeductibleBaseSource
): boolean => {
  if (!isLaserSource(effective.source)) return false;
  if (effective.source !== "REVIEW_DECISION") return true;
  const baseAmount = policy?.specific_benefit?.specific_deductible;
  return typeof baseAmount !== "number" || effective.amount !== baseAmount;
};

/**
 * The only part of a policy this resolver reads: the base specific deductible,
 * used as the final fallback in the precedence chain.
 *
 * Typed structurally rather than as `Policy` so that callers holding just the
 * base amount can route through the single resolver instead of keeping a private
 * copy of the precedence logic. The Claims Summary and Watch List calculators are
 * exactly that case (#6929): they receive a `specificDeductible: number` from the
 * selected policy, and before this they each re-derived the precedence — badly,
 * missing contract lasers and exclusions entirely. A full `Policy` is still
 * assignable, so the five component callers are unaffected.
 */
export type DeductibleBaseSource = {
  specific_benefit?: { specific_deductible?: number | null } | null;
};

export const getEffectiveDeductible = (
  policyData?: PolicyData,
  policy?: DeductibleBaseSource
): { amount: number; label: string; source: DeductibleSource; reviewPending: boolean } => {
  // An open deductible review (Feature #3502): the amount below is the last
  // resolved deductible, held in force, and stop-loss claims are on hold.
  const reviewPending = !!policyData?.is_deductible_review_pending;
  // `source` names the precedence branch that actually applies, and applies the
  // SAME `> 0` amount guards as the resolution below — so an INCOMPLETE laser
  // (flag set but amount missing/0: the #5901 split-brain) resolves to BASE, not
  // a false "Lasered"/"Contract" badge over a base amount (#5901 AC#1). `source`,
  // `label`, and `amount` therefore can't disagree (single source of truth — the
  // #5900 review finding). Amount still prefers the BE-resolved value below.
  const contractDeductible = policyData?.amt_contract_lasered_deductible;
  const laseredDeductible = policyData?.lasered_attributes?.amt_lasered_deductible;
  const fallbackSource: DeductibleSource = policyData?.is_contract_excluded
    ? "EXCLUDED"
    : policyData?.is_contract_lasered && contractDeductible && contractDeductible > 0
      ? "CONTRACT"
      : policyData?.is_lasered && laseredDeductible && laseredDeductible > 0
        ? "LASER"
        : "BASE";
  const fallbackLabel = SOURCE_LABEL[fallbackSource];

  // Prefer the BE-resolved effective deductible (full precedence, incl. the
  // valid 0 for a fully excluded claimant). typeof-number accepts 0 but rejects
  // BOTH the omitted field (undefined) and a defensive future explicit null —
  // either falls through to the FE fallback rather than returning a null amount.
  //
  // The `> 0 || is_contract_excluded` guard mirrors the `> 0` guards the FE
  // fallback below already applies, and closes a divergence this resolver would
  // otherwise create (Dillon, !4690 review).
  //
  // A stored `effective_specific_deductible` of 0 is only MEANINGFUL for a fully
  // excluded claimant. It is also reachable WITHOUT exclusion: policy_data.sql
  // resolves the lasered branch as COALESCE(li.lasered_deductible,
  // tp.specific_deductible), and COALESCE only replaces NULL — a stored literal 0
  // passes straight through. The UI can persist that 0 today via
  // `parseFloat(...) || 0` (ClaimantCreateInputs.tsx, LaseredClaimantDialog.tsx).
  //
  // Without this guard such a claimant resolved to 0 and was then dropped from
  // BOTH exhibit tables by their `claimantDeductible <= 0` guard — silently, and
  // while this function's own fallback already knows the right answer for the
  // identical claimant (an incomplete laser resolves to BASE). The resolver was
  // deferring to a BE value encoding a rule the resolver itself rejects.
  //
  // Not a regression from #6929 — before it, the calculators did
  // `lasered_attributes.amt ?? base` → 0 → filtered, so the cohort was dropped
  // either way. This closes it rather than inheriting it.
  const beEffectiveDeductible = policyData?.effective_specific_deductible;
  if (typeof beEffectiveDeductible === "number" && (beEffectiveDeductible > 0 || policyData?.is_contract_excluded)) {
    // With the server amount, take the server's source too when it sent one
    // (#3512): only SQL knows whether a conditional trigger, a modification or a
    // review decision won on recency. Older snapshots omit it → fallback branch.
    const serverSource = policyData?.effective_deductible_source
      ? SERVER_DEDUCTIBLE_SOURCE[policyData.effective_deductible_source]
      : undefined;
    const source = serverSource ?? fallbackSource;
    return { amount: beEffectiveDeductible, label: SOURCE_LABEL[source], source, reviewPending };
  }

  const source = fallbackSource;
  const label = fallbackLabel;

  // FE fallback for stale/absent snapshots (#5196) — same precedence + guards.
  if (policyData?.is_contract_excluded) {
    return { amount: 0, label, source, reviewPending };
  }
  if (policyData?.is_contract_lasered && contractDeductible && contractDeductible > 0) {
    return { amount: contractDeductible, label, source, reviewPending };
  }
  if (policyData?.is_lasered && laseredDeductible && laseredDeductible > 0) {
    return { amount: laseredDeductible, label, source, reviewPending };
  }
  return { amount: policy?.specific_benefit?.specific_deductible ?? 0, label, source, reviewPending };
};
